import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Briefcase, Shield, Loader2, Mail, Lock, User } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { PasswordInput } from '../components/PasswordInput';

var API_URL = process.env.REACT_APP_BACKEND_URL;
var GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function formatApiError(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    var msgs = [];
    for (var i = 0; i < detail.length; i++) {
      if (detail[i] && typeof detail[i].msg === "string") {
        msgs.push(detail[i].msg);
      }
    }
    return msgs.join(" ") || null;
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  var navigate = useNavigate();
  var auth = useAuth();
  var translation = useTranslation();
  var t = translation.t;
  var i18n = translation.i18n;
  var [searchParams] = useSearchParams();
  var googleBtnRef = React.useRef(null);

  var modeState = useState('login');
  var mode = modeState[0];
  var setMode = modeState[1];

  useEffect(function() {
    if (searchParams.get('mode') === 'register') setMode('register');
  }, []);

  var emailState = useState('');
  var email = emailState[0];
  var setEmail = emailState[1];

  var passwordState = useState('');
  var password = passwordState[0];
  var setPassword = passwordState[1];

  var firstNameState = useState('');
  var firstName = firstNameState[0];
  var setFirstName = firstNameState[1];

  var lastNameState = useState('');
  var lastName = lastNameState[0];
  var setLastName = lastNameState[1];

  var loadingState = useState(false);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState('');
  var error = errorState[0];
  var setError = errorState[1];

  function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    var returnUrl = sessionStorage.getItem('returnAfterLogin') || '/dashboard';

    if (mode === 'login') {
      axios.post(API_URL + '/api/auth/login', {
        email: email,
        password: password
      }, { withCredentials: true })
        .then(function(res) {
          auth.setUser(res.data);
          toast.success(t('login.success'));
          sessionStorage.removeItem('returnAfterLogin');
          navigate(returnUrl);
        })
        .catch(function(err) {
          var msg = err.response && err.response.data && err.response.data.detail;
          setError(formatApiError(msg) || t('login.error'));
          setLoading(false);
        });
    } else {
      axios.post(API_URL + '/api/auth/register', {
        email: email,
        password: password,
        first_name: firstName,
        last_name: lastName
      }, { withCredentials: true })
        .then(function(res) {
          auth.setUser(res.data);
          toast.success(t('login.accountCreated'));
          sessionStorage.removeItem('returnAfterLogin');
          navigate(returnUrl);
        })
        .catch(function(err) {
          var msg = err.response && err.response.data && err.response.data.detail;
          setError(formatApiError(msg) || t('login.error'));
          setLoading(false);
        });
    }
  }

  function handleGoogleResponse(resp) {
    if (!resp || !resp.credential) return;
    setError('');
    setLoading(true);
    var returnUrl = sessionStorage.getItem('returnAfterLogin') || '/dashboard';
    axios.post(API_URL + '/api/auth/google', {
      credential: resp.credential
    }, { withCredentials: true })
      .then(function(res) {
        auth.setUser(res.data);
        toast.success(t('login.success'));
        sessionStorage.removeItem('returnAfterLogin');
        navigate(returnUrl);
      })
      .catch(function(err) {
        var msg = err.response && err.response.data && err.response.data.detail;
        setError(formatApiError(msg) || t('login.error'));
        setLoading(false);
      });
  }

  useEffect(function() {
    if (!GOOGLE_CLIENT_ID) return;

    function renderGoogleButton() {
      if (!window.google || !window.google.accounts || !window.google.accounts.id) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse
      });
      if (googleBtnRef.current) {
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'filled_black',
          size: 'large',
          shape: 'pill',
          text: 'continue_with',
          logo_alignment: 'center',
          width: 320,
          locale: i18n.language
        });
      }
    }

    if (document.getElementById('google-gsi-script')) {
      renderGoogleButton();
      return;
    }
    var script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.id = 'google-gsi-script';
    script.onload = renderGoogleButton;
    document.body.appendChild(script);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-screen bg-[#050505] flex items-center justify-center px-6 pt-20"
      data-testid="login-page"
    >
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1
            className="text-3xl sm:text-4xl font-bold text-white mb-4"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            {mode === 'login' ? t('login.title') : t('login.register')}
          </h1>
          <p className="text-zinc-400">GuideMatch Tokyo</p>
        </div>

        <Card className="bg-zinc-900/50 border-white/10 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {mode === 'register' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 mb-2 block">{t('login.firstName')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                    <Input
                      type="text"
                      value={firstName}
                      onChange={function(e) { setFirstName(e.target.value); }}
                      placeholder={t('login.firstNamePlaceholder')}
                      required
                      className="bg-black/20 border-white/10 text-white pl-10 py-6"
                      data-testid="first-name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-300 mb-2 block">{t('login.lastName')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                    <Input
                      type="text"
                      value={lastName}
                      onChange={function(e) { setLastName(e.target.value); }}
                      placeholder={t('login.lastNamePlaceholder')}
                      required
                      className="bg-black/20 border-white/10 text-white pl-10 py-6"
                      data-testid="last-name-input"
                    />
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label className="text-zinc-300 mb-2 block">{t('becomeHost.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                <Input
                  type="email"
                  value={email}
                  onChange={function(e) { setEmail(e.target.value); }}
                  placeholder={t('login.emailPlaceholder')}
                  required
                  className="bg-black/20 border-white/10 text-white pl-10 py-6"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-300 mb-2 block">{t('login.passwordLabel')}</Label>
              <PasswordInput
                leftIcon={<Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />}
                value={password}
                onChange={function(e) { setPassword(e.target.value); }}
                placeholder="••••••••"
                required
                minLength={6}
                className="bg-black/20 border-white/10 text-white py-6"
                data-testid="password-input"
              />
            </div>

            <div className="text-right -mt-3">
              <button
                type="button"
                onClick={function() { navigate('/forgot-password'); }}
                className="text-sm text-zinc-400 hover:text-[#00F0FF] transition-colors"
                data-testid="forgot-password-link"
              >
                {t('forgotPw.title')}
              </button>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#F9004D] hover:bg-[#D00040] text-white rounded-full py-6 text-lg font-bold shadow-[0_0_15px_rgba(249,0,77,0.4)] hover:shadow-[0_0_25px_rgba(249,0,77,0.6)] transition-all duration-300 disabled:opacity-50"
              data-testid="submit-button"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                mode === 'login' ? t('login.submit') : t('login.submitRegister')
              )}
            </Button>
          </form>

          {GOOGLE_CLIENT_ID && (
            <div className="mt-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-zinc-500 text-sm">{t('login.or')}</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <div
                ref={googleBtnRef}
                className="flex justify-center"
                data-testid="google-signin"
              />
            </div>
          )}

          <div className="mt-6 text-center">
            {mode === 'login' ? (
              <p className="text-zinc-400">
                {t('login.noAccount')}{' '}
                <button
                  onClick={function() { setMode('register'); setError(''); }}
                  className="text-[#F9004D] hover:underline font-semibold"
                  data-testid="switch-to-register"
                >
                  {t('login.createAccount')}
                </button>
              </p>
            ) : (
              <p className="text-zinc-400">
                {t('login.alreadyAccount')}{' '}
                <button
                  onClick={function() { setMode('login'); setError(''); }}
                  className="text-[#F9004D] hover:underline font-semibold"
                  data-testid="switch-to-login"
                >
                  {t('login.connect')}
                </button>
              </p>
            )}
          </div>
        </Card>

        <div className="mt-8 text-center">
          <p className="text-zinc-500 text-sm mb-4">
            {t('login.isHost')}
          </p>
          <Button
            onClick={function() { navigate('/become-host'); }}
            variant="outline"
            className="border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/10"
          >
            <Briefcase className="h-4 w-4 mr-2" />
            {t('login.applyHost')}
          </Button>
        </div>

        {/* Secret Admin Link */}
        <div className="mt-12 text-center">
          <button
            onClick={function() { navigate('/admin-portal'); }}
            className="text-zinc-800 hover:text-zinc-700 text-xs opacity-10 hover:opacity-20 transition-opacity"
          >
            <Shield className="h-3 w-3 inline" />
          </button>
        </div>
      </div>
    </div>
  );
}
